import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getPageContent } from "../../utils";
import {
    selectCorporateArtPortalData,
    selectCorporateArtPortalStatus,
    selectCorporateArtPortalError,
    initializeAsync,
} from "./CorporateArtPortalSlice";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import { CardContainer } from "../../components/CardContainer";
import { Typography, Button, ImageList, ImageListItem, Skeleton, Stack } from "@mui/material";
import Link from "@mui/material/Link";
import { useLocation, useNavigate } from "react-router-dom";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InfoIcon from "@mui/icons-material/Info";
import InfoTwoToneIcon from "@mui/icons-material/InfoTwoTone";
import { useAuth } from "../../hooks/useAuth";
import { query } from "../../services/object";
import { get } from "../../api";
import { ImageGrid } from "../../components/ImageGrid";
import { useMediaQuery } from "@mui/material";
import { InnerCard } from "./InnerCard";
import { SimpleMasonry } from "../../components/SimpleMasonry";

const CorporateArtPortal = () => {
    const data = useSelector(selectCorporateArtPortalData);
    const status = useSelector(selectCorporateArtPortalStatus);
    const error = useSelector(selectCorporateArtPortalError);
    const dispatch = useDispatch();

    const { getUserDetailsNotAsync } = useAuth();
    const location = useLocation();
    const userDetails = getUserDetailsNotAsync();
    const navigate = useNavigate();
    const [favouriteArts, setFavouriteArts] = useState([]);
    const [orderList, setOrderList] = useState([]);
    const [loadingFavouriteArts, setLoadingFavouriteArts] = useState(true);
    const [loadingOrders, setLoadingOrders] = useState(true);
    const matches = useMediaQuery("(min-width:600px)");

    useEffect(() => {
        dispatch(initializeAsync({ userDetails: userDetails }));
    }, [location.pathname]);

    useEffect(() => {
        if (status === "idle") {
            setLoadingFavouriteArts(true);
            setLoadingOrders(true);
            dispatch(initializeAsync({ userDetails: userDetails }));
        }
    }, [status, dispatch]);

    useEffect(() => {
        if (data?.data?.favourite) {
            setFavouriteArts(data.data.favourite);
        }
        if (data?.data?.opportunity) {
            setOrderList(data.data.opportunity);
        }
        if (data?.data) {
            setLoadingFavouriteArts(false);
            setLoadingOrders(false);
        }
    }, [data]);

    const handleClick = (event, action) => {
        let path = event.target.id;
        if (action === "edit") {
            navigate("/" + path + "?enableFavorite=true", { replace: false });
        } else {
            navigate("/" + path, { replace: false });
        }
    };

    const getHeaderContent = () => {
        return (
            <Box sx={{}}>
                <Typography variant="h3" component="div">
                    {" "}
                    Welcome{" "}
                    <span
                        style={{
                            color: "#0080ff",
                        }}>
                        {userDetails?.contact?.accountName},
                    </span>{" "}
                </Typography>
                <Typography variant="h3" component="div">
                    {" "}
                    to your Corporate Art Portal
                </Typography>

                <Typography sx={{ mt: 2 }} variant="body1" component="div">
                    Here, you can effortlessly browse through the extensive collection of unique and inspiring artworks,
                    which you can carefully curate for your own unique spaces and environment.
                </Typography>
            </Box>
        );
    };

    const getOrderDetails = () => {
        if (loadingOrders) {
            let content = [1, 2, 3];
            return (
                <Box
                    sx={{
                        mb: 2,
                    }}>
                    {content?.map((item) => (
                        <Skeleton sx={{ mb: 2 }} variant="rounded" width="100%" height="100px" />
                    ))}
                </Box>
            );
        }
        if (orderList?.length > 0) {
            let records = orderList.map((item) => {
                return {
                    id: item.id,
                    companyName: item.name,
                    streetAddress: item.streetAddress,
                    pieces: item.artPieces,
                    donation: "$TBD",
                    status: item.status,
                };
            });

            return records?.map((item, index) => (
                <Box
                    sx={{
                        mb: 2,
                    }}>
                    <InnerCard content={item}></InnerCard>
                </Box>
            ));
        }

        return (
            <Box align="center" sx={{ mt: 2, py: 2 }}>
                <Typography component="div" variant="h5" color="text.disabled" gutterBottom sx={{ maxWidth: "32ch" }}>
                    Your Completed and In-Progress projects will appear here.
                </Typography>
                <Button sx={{ mt: 4 }} variant="outlined" onClick={(e) => handleClick(e)} id="donationOrder">
                    Start your first project
                </Button>
            </Box>
        );
    };

    const getFavoritesDetails = () => {
        if (loadingFavouriteArts) {
            let content = [1, 2, 3, 4, 5];
            return (
                <ImageList sx={{ mt: "0px" }} cols={matches ? 3 : 2} gap={1}>
                    {content?.map((item) => (
                        <ImageListItem sx={{ px: 0.5 }} key={item}>
                            <Skeleton variant="rounded" width="100%" height="25vh" />
                        </ImageListItem>
                    ))}
                </ImageList>
            );
        }
        if (favouriteArts?.length > 0) {
            let records = favouriteArts.map((item) => {
                return {
                    id: item.id,
                    title: item.art.name,
                    artId: item.art.id,
                    img: process.env.REACT_APP_CLOUD_FRONT_URL + item.art?.publishedAssetLink?.asset?.fileId,
                };
            });
            return <SimpleMasonry content={records}></SimpleMasonry>;
        }
        return (
            <Box align="center" sx={{ mt: 2, py: 2 }}>
                <Typography variant="h5" align="center" color="text.disabled" gutterBottom sx={{ maxWidth: "24ch" }}>
                    Your favorite art work will appear here
                </Typography>
                <Button sx={{ mt: 4 }} variant="outlined" onClick={(e) => handleClick(e)} id="favourite">
                    Browse gallery
                </Button>
            </Box>
        );
    };

    const displayWelcomeMsg = () => {
        return (
            <Grid item xs={12}>
                <CardContainer height="auto" contentOnCenter={false}>
                    {getHeaderContent()}
                </CardContainer>
            </Grid>
        );
    };

    const displayOrders = () => {
        return (
            <Grid item xs={12} sx={{ mt: 3 }}>
                <CardContainer
                    height="calc(100vh - 14.9rem)"
                    contentOnCenter={orderList?.length > 0 || loadingOrders ? false : true}
                    py="4px"
                    pb="24px"
                    title={
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}>
                            <Typography component="div" variant="subtitle2" color="text.disabled">
                                Your projects
                            </Typography>
                        </Box>
                    }>
                    <Box
                        sx={
                            orderList?.length > 0 || loadingOrders
                                ? { position: "relative" }
                                : {
                                      position: "relative",
                                      height: "100%",
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      overflow: "scroll",
                                  }
                        }>
                        <Box sx={{ position: "relative", width: "100%" }}>{getOrderDetails()}</Box>
                    </Box>
                </CardContainer>
            </Grid>
        );
    };

    const displayFavouriteArts = () => {
        return (
            <Grid item xs={12}>
                <CardContainer
                    height="calc(100vh - 4.5rem)"
                    py="4px"
                    pb="24px"
                    title={
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}>
                            <Typography component="div" variant="subtitle2" color="text.disabled">
                                Your favorites
                            </Typography>
                            <Stack direction={"row"} spacing={1}>
                                <Typography variant="body2" color={"text.secondary"}>
                                    {favouriteArts?.length}
                                    {" Selected"}
                                </Typography>
                                <Link underline="always" onClick={(e) => handleClick(e, "edit")} id="favourite">
                                    Edit favorites
                                </Link>
                            </Stack>
                        </Box>
                    }
                    contentOnCenter={favouriteArts?.length > 0 || loadingFavouriteArts ? false : true}>
                    {getFavoritesDetails()}
                </CardContainer>
            </Grid>
        );
    };

    let body = (
        <Box sx={{ p: "2rem" }}>
            <Grid container spacing={3} sx={{ maxHeight: "calc(100vh - 2.5rem)" }}>
                <Grid item md={6} xs={12} spacing={3}>
                    {displayWelcomeMsg()}
                    {displayOrders()}
                </Grid>
                <Grid item md={6} xs={12} spacing={3}>
                    {displayFavouriteArts()}
                </Grid>
            </Grid>
        </Box>
    );

    return getPageContent(body, status, error);
};

export { CorporateArtPortal };
